import { listVacanciesAdapter } from '../apollo/adapters/modules/listVacanciesAdapter'
import { vacancyFiltersAdapter } from '../apollo/adapters/modules/vacancyFiltersAdapter'
import {
  VACANCIES_QUERY,
  LISTS_VACANCY_FILTERS,
} from '../config/graphql-queries'

type Query = { [key: string]: string }

const HH_SECTION_TYPES = [
  'sectionSearchVacancies',
  'sectionVacanciesHero',
  'sectionVacanciesList',
]

const buildVacancyGqlFilter = (
  vacanciesQuery: Query,
  specialityId?: string | number | null
) => {
  const filter: any = {
    AND: [],
    iblock: { code: { equals: 'vacancy' } },
  }

  if (vacanciesQuery.search) {
    filter.AND.push({
      OR: [
        { name: { contains: vacanciesQuery.search } },
        {
          propertyValuesRelation: {
            some: {
              targetElement: { name: { contains: vacanciesQuery.search } },
            },
          },
        },
      ],
    })
  }

  if (vacanciesQuery.region) {
    filter.AND.push({
      propertyValuesRelation: {
        some: {
          property: { code: { equals: 'region' } },
          targetElement: { id: { equals: vacanciesQuery.region } },
        },
      },
    })
  }

  const speciality = specialityId ?? vacanciesQuery.speciality
  if (speciality) {
    filter.AND.push({
      propertyValuesRelation: {
        some: {
          property: { code: { equals: 'specialization' } },
          targetElement: { id: { equals: String(speciality) } },
        },
      },
    })
  }

  return filter
}

const startCareerVacanciesRequest = async (
  vacanciesQuery: Query,
  app: any,
  component: any,
  isInitial: boolean
) => {
  const specialityId = component?.value?.sectionDataLocal?.speciality?.id
  const filter = buildVacancyGqlFilter(vacanciesQuery, specialityId)

  const [vacanciesResponse, filtersResponse] = await Promise.all([
    app.$api.requests.sendGraphqlQuery({
      query: VACANCIES_QUERY,
      variables: { filter, first: isInitial ? 3 : 5 },
    }),
    app.$api.requests.sendGraphqlQuery({ query: LISTS_VACANCY_FILTERS }),
  ])

  const elements = vacanciesResponse?.data?.elements ?? []
  const elementsCount = vacanciesResponse?.data?.elementsCount ?? 0
  const regions = filtersResponse?.data?.regions ?? []
  const specialties = filtersResponse?.data?.specialties ?? []

  return {
    initialListVacancies: listVacanciesAdapter(elements),
    initialTotalCountVacancies: elementsCount,
    listRegions: vacancyFiltersAdapter(regions),
    listSpecialties: vacancyFiltersAdapter(specialties),
    searchError: false,
  }
}

const hhVacanciesRequest = async (
  vacanciesQuery: Query,
  app: any,
  isInitial: boolean
) => {
  const bitrix = app.$api.bitrix.requests

  const params: {
    region?: number
    specialization?: number
    name?: string
    page: number
    size: number
  } = { page: 1, size: isInitial ? 3 : 5 }

  if (vacanciesQuery.search) params.name = vacanciesQuery.search
  if (vacanciesQuery.region) params.region = parseInt(vacanciesQuery.region, 10)
  if (vacanciesQuery.speciality)
    params.specialization = parseInt(vacanciesQuery.speciality, 10)

  const [vacanciesResponse, specializationsResponse, regionsResponse] =
    await Promise.all([
      bitrix.getHhVacancies(params),
      bitrix.getHhSpecializations(),
      bitrix.getHhRegions(),
    ])

  return {
    vacanciesList: vacanciesResponse.data?.data?.vacancies ?? [],
    vacanciesCount: vacanciesResponse.data?.data?.navigation?.total ?? 0,
    adaptedRegions: vacancyFiltersAdapter(regionsResponse.data?.data ?? []),
    adaptedSpecialties: vacancyFiltersAdapter(
      specializationsResponse.data?.data ?? []
    ),
  }
}

export const searchVacanciesRequest = async (
  vacanciesQuery: Query,
  app: any,
  components: [any],
  isInitial = false
) => {
  const startCareerComponent = components.find(
    (component) => component.type === 'sectionSearchVacanciesStartCareer'
  )
  const needHh = components.some((component) =>
    HH_SECTION_TYPES.includes(component.type)
  )

  const [startCareerData, hhData] = await Promise.all([
    startCareerComponent
      ? startCareerVacanciesRequest(
          vacanciesQuery,
          app,
          startCareerComponent,
          isInitial
        )
      : Promise.resolve(null),
    needHh
      ? hhVacanciesRequest(vacanciesQuery, app, isInitial)
      : Promise.resolve(null),
  ])

  return {
    components: components.map((component) => {
      if (component.type === 'sectionSearchVacanciesStartCareer') {
        return startCareerData
          ? { ...component, value: { ...component.value, ...startCareerData } }
          : component
      }

      if (!hhData) return component

      if (component.type === 'sectionSearchVacancies') {
        return {
          ...component,
          value: {
            ...component.value,
            initialListVacancies: listVacanciesAdapter(hhData.vacanciesList),
            listRegions: hhData.adaptedRegions,
            listSpecialties: hhData.adaptedSpecialties,
            initialTotalCountVacancies: hhData.vacanciesCount,
            searchError: false,
          },
        }
      }
      if (component.type === 'sectionVacanciesHero') {
        return {
          ...component,
          value: {
            ...component.value,
            listRegions: hhData.adaptedRegions,
            listSpecialties: hhData.adaptedSpecialties,
          },
        }
      }
      if (component.type === 'sectionVacanciesList') {
        return {
          ...component,
          value: {
            ...component.value,
            listVacancies: listVacanciesAdapter(hhData.vacanciesList),
          },
        }
      }
      return component
    }),
  }
}
